import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  CircularProgress,
  Typography,
  TextField,
  Button,
} from '@mui/material';
import _ from 'lodash';
import { useSnackbar } from 'notistack';

import '../../commonStyles.scss';
import logo from '../../utils/images/TagLogo_AllWhite.png';

import Phone from '@mui/icons-material/Phone';
import Email from '@mui/icons-material/Email';

import GenericModal from '../../components/GenericModal/GenericModal';
import GenericSpinner from '../../components/GenericSpinner/GenericSpinner';

import { useAppStore } from '../../stores';

import { callLoginApi, generateEmailApi } from './services';
import { configureInterceptors } from '../../globalAxiosCall';
import { makeRequestForUserPermissions } from '../../actions/UserAction/UserActions';

interface LoginProps {
  inputStyles?: object;
  isMobile?: boolean;
  toggleForm?: () => void;
  onSwitchToRegister?: () => void;
}

const Login: React.FC<LoginProps> = (props) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const setLoginSuccess = useAppStore((state) => state.setLoginSuccess);
  const setLoginError = useAppStore((state) => state.setLoginError);
  const setUser = useAppStore((state) => state.setUser);
  const setUserRefreshToken = useAppStore((state) => state.setUserRefreshToken);
  const setUserJwtToken = useAppStore((state) => state.setUserJwtToken);
  const setUserPermissions = useAppStore((state) => state.setUserPermissions);
  const setActiveConvention = useAppStore((state) => state.setActiveConvention);
  const setAttendeeRecord = useAppStore((state) => state.setAttendeeRecord);

  const [recoveryEmail, setRecoveryEmail] = useState('none');
  const [open, setOpen] = useState(false);
  const [wasResetPasswordClicked, setWasResetPasswordClicked] = useState(false);
  const [isLoginPending, setIsLoginPending] = useState(false);
  const [wasResetPasswordFormSubmitted, setWasResetPasswordFormSubmitted] = useState(false);
  const [shouldDisplayEmailSentMessage, setShouldDisplayEmailSentMessage] = useState(false);

  const handleOpen = () => {
    setWasResetPasswordClicked(false);
    setOpen(true);
  };

  const handleClose = () => {
    setWasResetPasswordFormSubmitted(false);
    setShouldDisplayEmailSentMessage(false);
    setOpen(false);
  };

  const login = async (username: string, password: string) => {
    setIsLoginPending(true);
    try {
      const response = await callLoginApi(username, password);
      const data = response.data;
      window.sessionStorage.setItem('loggedIn', 'true');
      setAttendeeRecord(data.attendeeRecord);
      setUser(data.userRecord);
      setActiveConvention(data.activeConvention);
      setUserRefreshToken(data.refreshToken);
      setUserJwtToken(data.jwtToken); // Store JWT token from response
      configureInterceptors(data.refreshToken);

      const permissionsResponse = await makeRequestForUserPermissions({
        id: data.id,
        Username: username,
      });
      setUserPermissions(permissionsResponse.data);
      setLoginSuccess();
      setIsLoginPending(false);
      navigate('/');
    } catch (error: unknown) {
      setIsLoginPending(false);
      setLoginError();
      if (
        _.get(error, 'response.status') &&
        (error as { response: { status: number } }).response.status === 401
      ) {
        if (_.get(error, 'response.data.registrationClosed')) {
          enqueueSnackbar((_.get(error, 'response.data.message') as unknown as string) || 'Registration is closed', {
            variant: 'error',
            anchorOrigin: { vertical: 'top', horizontal: 'left' },
          });
        } else {
          enqueueSnackbar('Invalid Login. Unauthorized', {
            variant: 'error',
            anchorOrigin: { vertical: 'top', horizontal: 'left' },
          });
        }
      } else {
        enqueueSnackbar(String(error), { variant: 'error' });
      }
    }
  };

  const handleLoginClick = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const username = formData.get('username') as string;
    const password = formData.get('password') as string;
    if (username !== '' && password !== '') {
      login(username, password);
    }
  };

  const submitForgotPasswordForm = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setWasResetPasswordFormSubmitted(true);
    const formData = new FormData(e.currentTarget);
    const email = formData.get('recoveryEmail') as string;
    setRecoveryEmail(email);
    generateEmailApi(email, () => {
      setWasResetPasswordFormSubmitted(false);
      setShouldDisplayEmailSentMessage(true);
    });
  };

  const handleSwitchToRegister = () => {
    if (props.onSwitchToRegister) {
      props.onSwitchToRegister();
    } else if (props.toggleForm) {
      props.toggleForm();
    }
  };

  const renderForgotPasswordFlow = () => {
    if (shouldDisplayEmailSentMessage) {
      return (
        <div style={{ textAlign: 'center' }}>
          <span style={{ color: 'green' }}>
            If your account exists you will receive an email at&nbsp;
            <b>{recoveryEmail}</b>. <br />
            If you do not receive an email within 30 minutes please check your
            Spam Folder <br />
            or contact AFT at&nbsp;
            <Link to="#">AFTConvention@aft.org</Link> or&nbsp;
            <Link to="tel:+18002381133,3421">1-800-238-1133 EXT. 3421</Link>.
          </span>
        </div>
      );
    }
    if (!wasResetPasswordFormSubmitted) {
      return (
        <div>
          <span>Please enter the email associated with your account.</span>
          <div className="reset-password-form">
            <form
              method="post"
              onSubmit={submitForgotPasswordForm}
              style={{
                width: '100%',
                marginBottom: '2rem',
              }}
            >
              <div className="pr-2">
                <TextField
                  className="resetpsswd-modal"
                  id="forgot-password-email-reset"
                  label="Email"
                  name="recoveryEmail"
                  required={true}
                  autoComplete="forgotPasswordEmail"
                  margin="normal"
                  variant="outlined"
                />
              </div>
              <div>
                <Button
                  type="submit"
                  size="large"
                  variant="contained"
                  color="primary"
                >
                  <span style={{ padding: '5px 8px' }}>Submit</span>
                </Button>
              </div>
            </form>
          </div>
        </div>
      );
    } else if (wasResetPasswordFormSubmitted) {
      return <GenericSpinner includeOverlay={false} padding={0} />;
    }
    return null;
  };

  return (
    <div className="login-container-new">
      <div className="login-header-section">
        <h2 className="login-title">Welcome back</h2>
        <p className="login-subtitle">Sign into your account</p>
      </div>

      <form method="post" onSubmit={handleLoginClick} className="login-form-new">
        <div className="login-field">
          <TextField
            name="username"
            type="text"
            autoComplete="off"
            required={true}
            placeholder="Username"
            variant="outlined"
            fullWidth
          />
        </div>

        <div className="login-field">
          <TextField
            name="password"
            type="password"
            autoComplete="off"
            required={true}
            placeholder="Password"
            variant="outlined"
            fullWidth
          />
        </div>

        <div className="login-button-wrapper">
          <Button
            className="login-submit-btn"
            type="submit"
            size="large"
            variant="contained"
            color="primary"
            disabled={isLoginPending}
            fullWidth
          >
            {isLoginPending ? (
              <CircularProgress size={24} style={{ color: 'white' }} />
            ) : (
              <Typography>Login</Typography>
            )}
          </Button>
        </div>

        <div className="login-links">
          <a
            href="#"
            className="login-link"
            onClick={(e) => {
              e.preventDefault();
              setOpen(true);
              setWasResetPasswordClicked(true);
            }}
          >
            Reset password
          </a>
          <a
            href="#"
            className="login-link"
            onClick={(e) => {
              e.preventDefault();
              handleOpen();
            }}
          >
            Having trouble? Click here for help
          </a>
        </div>
      </form>

      <div className="login-register-prompt">
        <span>Don't have an account?</span>
        <a
          href="#"
          className="login-register-link"
          onClick={(e) => {
            e.preventDefault();
            handleSwitchToRegister();
          }}
        >
          Register here
        </a>
      </div>

      <GenericModal
        open={open}
        onClose={handleClose}
        title={
          !wasResetPasswordClicked
            ? 'Having Trouble Logging In?'
            : 'Reset Password'
        }
        content={
          <div className="p-3">
            {props.isMobile && (
              <div className="mobile-help-modal-logo-wrapper text-center">
                <img src={logo} className="aft-logo" alt="aft-logo" />
              </div>
            )}
            <div className="modal-wrapper text-center">
              {!wasResetPasswordClicked ? (
                <div>
                  <div className="modal-description">
                    Please contact us using one of the options below.
                  </div>
                  <div className="modal-phone">
                    <Phone fontSize="small" />
                    <span className="pl-2">
                      Phone: <Link to="tel:+18002381133,3421">1-800-238-1133 EXT. 3421</Link>
                    </span>
                  </div>
                  <div className="modal-email">
                    <Email fontSize="small" />
                    <span className="pl-2">
                      Email: <Link to="#">AFTConvention@aft.org</Link>
                    </span>
                  </div>
                </div>
              ) : (
                renderForgotPasswordFlow()
              )}
            </div>
          </div>
        }
      />
    </div>
  );
};

export default Login;
